/**
 * DealNexa API Service
 * Centralized API client with Supabase JWT authentication
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

// Module-level token store — set explicitly from App.js via setApiToken()
let _token = null;

export function setApiToken(token) {
  _token = token;
}

export function getApiToken() {
  return _token;
}

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token from module-level store
api.interceptors.request.use((config) => {
  if (_token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${_token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

// ==================== DOCUMENTS ====================

export const documentsApi = {
  // List all documents
  list: () => api.get('/api/documents'),
  
  // Get single document
  get: (id) => api.get(`/api/documents/${id}`),
  
  // Delete document
  delete: (id) => api.delete(`/api/documents/${id}`),
  
  // Update document category
  updateCategory: (id, category) => 
    api.put(`/api/documents/${id}/category`, { category }),
  
  // Link/unlink document to deal
  linkDeal: (id, dealId, dealName = null) => 
    api.put(`/api/documents/${id}/deal`, { dealId, dealName }),
  
  // Bulk delete documents
  bulkDelete: (documentIds) => 
    api.post('/api/documents/bulk/delete', { documentIds }),
  
  // Bulk update categories
  bulkUpdateCategory: (documentIds, category) => 
    api.post('/api/documents/bulk/category', { documentIds, category }),
  
  // Bulk link to deal
  bulkLinkDeal: (documentIds, dealId, dealName) => 
    api.post('/api/documents/bulk/deal', { documentIds, dealId, dealName }),
  
  // Upload document (uses FormData)
  upload: (file, onProgress, category) => {
    const formData = new FormData();
    formData.append('document', file);
    if (category) formData.append('category', category);

    return api.post('/api/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress ? (e) => {
        const progress = Math.round((e.loaded * 100) / e.total);
        onProgress(progress);
      } : undefined
    });
  },
  
  // Get document preview URL
  getPreviewUrl: (id) => `${API_URL}/api/documents/${id}/preview`,
  
  // Get document text
  getText: (id) => api.get(`/api/documents/${id}/text`),
  
  // Reanalyze document
  reanalyze: (id) => api.post(`/api/documents/${id}/reanalyze`),

  // Analyze document
  analyze: (id) => api.post(`/api/documents/${id}/analyze`),

  // Get findings for a document
  getFindings: (id) => api.get(`/api/documents/${id}/findings`),
};

// ==================== CLASSIFICATION (Module 1) ====================

// SOC line classification — lazy classify-on-open + review.
export const classificationApi = {
  status: (dealId) => api.get(`/api/deals/${dealId}/classification/status`),
  run: (dealId, force = false) => api.post(`/api/deals/${dealId}/classification/run`, { force }),
  lines: (dealId) => api.get(`/api/deals/${dealId}/classification/lines`),
  confirm: (dealId, items) => api.post(`/api/deals/${dealId}/classification/confirm`, { items }),
};

// Module 2 — Quality of Earnings (three-EBITDA bridge, walks, flags)
export const qofeApi = {
  get: (dealId) => api.get(`/api/deals/${dealId}/qofe`),
};

// Module 3 — LBO (Python engine via Node proxy)
export const lboApi = {
  run: (dealId, body) => api.post(`/api/deals/${dealId}/lbo/run`, body),
  list: (dealId) => api.get(`/api/deals/${dealId}/lbo`),
  get: (dealId, modelId) => api.get(`/api/deals/${dealId}/lbo/${modelId}`),
  lock: (dealId, modelId) => api.post(`/api/deals/${dealId}/lbo/${modelId}/lock`),
};

// Module 4 — DCF
export const dcfApi = {
  run: (dealId, body) => api.post(`/api/deals/${dealId}/dcf/run`, body),
  list: (dealId) => api.get(`/api/deals/${dealId}/dcf`),
};

// Module 5 — Sensitivity / Monte Carlo
export const sensitivityApi = {
  run: (dealId, body) => api.post(`/api/deals/${dealId}/sensitivity/run`, body),
};

// Module 8 — Export (live-formula xlsx / csv with provenance)
export const exportApi = {
  lbo: (dealId, modelId, format = 'csv') => api.post(`/api/deals/${dealId}/export/lbo/${modelId}`, { format }),
};

// ==================== DEALS ====================

export const dealsApi = {
  // List all deals
  list: () => api.get('/api/deals'),
  
  // Get single deal
  get: (id) => api.get(`/api/deals/${id}`),
  
  // Create deal
  create: (data) => api.post('/api/deals', data),
  
  // Update deal
  update: (id, data) => api.put(`/api/deals/${id}`, data),
  
  // Delete deal
  delete: (id) => api.delete(`/api/deals/${id}`),
  
  // Get deal documents
  getDocuments: (id) => api.get(`/api/deals/${id}/documents`),
  
  // Add document to deal
  addDocument: (dealId, jobId) => 
    api.post(`/api/deals/${dealId}/documents`, { jobId }),
  
  // Remove document from deal
  removeDocument: (dealId, docId) => 
    api.delete(`/api/deals/${dealId}/documents/${docId}`),
  
  // Get deal tasks
  getTasks: (id) => api.get(`/api/deals/${id}/tasks`),
  
  // Create task
  createTask: (dealId, data) => 
    api.post(`/api/deals/${dealId}/tasks`, data),
  
  // Update task
  updateTask: (dealId, taskId, data) => 
    api.put(`/api/deals/${dealId}/tasks/${taskId}`, data),
  
  // Delete task
  deleteTask: (dealId, taskId) => 
    api.delete(`/api/deals/${dealId}/tasks/${taskId}`),
  
  // Get deal contacts
  getContacts: (id) => api.get(`/api/deals/${id}/contacts`),

  // Create contact
  createContact: (dealId, data) => api.post(`/api/deals/${dealId}/contacts`, data),

  // Update contact
  updateContact: (dealId, contactId, data) => api.put(`/api/deals/${dealId}/contacts/${contactId}`, data),

  // Delete contact
  deleteContact: (dealId, contactId) => api.delete(`/api/deals/${dealId}/contacts/${contactId}`),
  
  // Get deal notes
  getNotes: (id) => api.get(`/api/deals/${id}/notes`),
  
  // Add note — accepts either a string (plain note) or an object (structured finding)
  addNote: (dealId, content) =>
    api.post(`/api/deals/${dealId}/notes`, typeof content === 'string' ? { content } : content),
  
  // Update note
  updateNote: (dealId, noteId, content) => 
    api.put(`/api/deals/${dealId}/notes/${noteId}`, { content }),
  
  // Delete note
  deleteNote: (dealId, noteId) => 
    api.delete(`/api/deals/${dealId}/notes/${noteId}`),
  
  // Get AI analysis/finding
  getAnalysis: (id) => api.get(`/api/deals/${id}/analysis`),

  // Trigger AI analysis (optionally with intake params)
  analyze: (id, params = {}) => api.post(`/api/deals/${id}/analyze`, params),

  // Analysis history
  getAnalysisHistory: (dealId) => api.get(`/api/deals/${dealId}/analyses`),
  getAnalysisById: (dealId, analysisId) => api.get(`/api/deals/${dealId}/analyses/${analysisId}`),
  
  // Get findings
  getFindings: (id) => api.get(`/api/deals/${id}/findings`),
  
  // Create finding
  createFinding: (dealId, data) => 
    api.post(`/api/deals/${dealId}/findings`, data),
  
  // Update finding
  updateFinding: (dealId, findingId, data) => 
    api.put(`/api/deals/${dealId}/findings/${findingId}`, data),
  
  // Delete finding
  deleteFinding: (dealId, findingId) =>
    api.delete(`/api/deals/${dealId}/findings/${findingId}`),

  // Team members
  getTeam: (dealId) => api.get(`/api/deals/${dealId}/team`),
  addTeamMember: (dealId, userId, role) => api.post(`/api/deals/${dealId}/team`, { userId, role }),
  removeTeamMember: (dealId, memberId) => api.delete(`/api/deals/${dealId}/team/${memberId}`),
};

// ==================== REPUTATION ====================

export const reputationApi = {
  getLatest: (dealId) => api.get(`/api/deals/${dealId}/reputation`),
  analyze: (dealId, data) => api.post(`/api/deals/${dealId}/reputation/analyze`, data),
  getResult: (dealId, id) => api.get(`/api/deals/${dealId}/reputation/result/${id}`),
};

// ==================== CONTACTS ====================

export const contactsApi = {
  // List all contacts
  list: () => api.get('/api/contacts'),
  
  // Get single contact
  get: (id) => api.get(`/api/contacts/${id}`),
  
  // Create contact
  create: (data) => api.post('/api/contacts', data),
  
  // Update contact
  update: (id, data) => api.put(`/api/contacts/${id}`, data),
  
  // Delete contact
  delete: (id) => api.delete(`/api/contacts/${id}`),
  
  // Import contacts
  import: (contacts) => api.post('/api/contacts/import', { contacts }),
  
  // Get contacts by deal
  getByDeal: (dealId) => api.get(`/api/contacts/deal/${dealId}`)
};

// ==================== DILIGENCE ====================

export const diligenceApi = {
  // Get items + categories for a deal
  getByDeal: (dealId) => api.get(`/api/diligence/deal/${dealId}`),

  // Categories
  createCategory: (dealId, data) =>
    api.post(`/api/deals/${dealId}/diligence/categories`, data),
  updateCategory: (dealId, categoryId, data) =>
    api.put(`/api/deals/${dealId}/diligence/categories/${categoryId}`, data),
  deleteCategory: (dealId, categoryId) =>
    api.delete(`/api/deals/${dealId}/diligence/categories/${categoryId}`),

  // Items
  createItem: (dealId, data) =>
    api.post(`/api/deals/${dealId}/diligence/items`, data),
  updateItem: (dealId, itemId, data) =>
    api.put(`/api/deals/${dealId}/diligence/items/${itemId}`, data),
  deleteItem: (dealId, itemId) =>
    api.delete(`/api/deals/${dealId}/diligence/items/${itemId}`),

  // Item documents
  addComment: (dealId, itemId, text, author) =>
    api.post(`/api/deals/${dealId}/diligence/items/${itemId}/comments`, { text, author }),
  addDocument: (dealId, itemId, documentId) =>
    api.post(`/api/deals/${dealId}/diligence/items/${itemId}/documents`, { documentId }),
  removeDocument: (dealId, itemId, documentId) =>
    api.delete(`/api/deals/${dealId}/diligence/items/${itemId}/documents/${documentId}`),
};

// ==================== ANNOTATIONS ====================

export const annotationsApi = {
  // Get annotations for a document
  getByDocument: (documentId) =>
    api.get(`/api/documents/${documentId}/annotations`),

  // Create annotation
  create: (data) => api.post(`/api/documents/${data.document_id}/annotations`, data),

  // Update annotation
  update: (id, data) => api.put(`/api/documents/${data.document_id}/annotations/${id}`, data),

  // Delete annotation (needs documentId)
  delete: (documentId, id) => api.delete(`/api/documents/${documentId}/annotations/${id}`)
};

// ==================== ORGANIZATIONS ====================

export const organizationsApi = {
  // Get current org
  getCurrent: () => api.get('/api/organizations/me'),
  
  // Get categories
  getCategories: () => api.get('/api/organizations/me/categories'),

  // Add category
  addCategory: (category) =>
    api.post('/api/organizations/me/categories', { category }),

  // Delete category
  deleteCategory: (category) =>
    api.delete(`/api/organizations/me/categories/${encodeURIComponent(category)}`),
  
  // Get org users
  getUsers: () => api.get('/api/organizations/me/users'),
  
  // Invite user
  inviteUser: (email, role) =>
    api.post('/api/organizations/me/invite', { email, role })
};

// Multi-org workspaces (consultant accounts): list memberships, switch active org, add org.
export const orgsApi = {
  list: () => api.get('/api/me/organizations'),
  switch: (orgId) => api.post('/api/me/active-org', { org_id: orgId }),
  add: (name, type) => api.post('/api/organizations', { name, type }),
};

// ==================== UNIFIED TASKS ====================

export const tasksApi = {
  list: (params = {}) => api.get('/api/tasks', { params }),
  markComplete: (id) => api.patch(`/api/tasks/${id}/complete`),
};


// ==================== USERS ====================

export const usersApi = {
  // Get org members for owner assignment
  list: () => api.get('/api/organizations/me/users'),
  
  // Get current user
  getMe: () => api.get('/api/auth/me'),
  
  // Update profile
  updateProfile: (data) => api.put('/api/auth/me', data)
};

// ==================== ACTIVITIES ====================

export const activitiesApi = {
  // Get recent activities
  list: (limit = 20) => api.get(`/api/activities?limit=${limit}`)
};

// ==================== CRM ====================

export const crmApi = {
  // ── Parse (step 1: detect columns, return preview — nothing saved) ──
  parse: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/api/crm/parse', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  // ── Import (step 2: save rows with confirmed mappings) ──
  import: (file, confirmedMappings = null) => {
    const form = new FormData();
    form.append('file', file);
    if (confirmedMappings) form.append('confirmedMappings', JSON.stringify(confirmedMappings));
    return api.post('/api/crm/import', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  // ── Companies ──
  listCompanies: (params = {}) => api.get('/api/crm/companies', { params }),
  getCompany: (id) => api.get(`/api/crm/companies/${id}`),
  createCompany: (data) => api.post('/api/crm/companies', data),
  updateCompany: (id, data) => api.put(`/api/crm/companies/${id}`, data),
  deleteCompany: (id) => api.delete(`/api/crm/companies/${id}`),
  getCompanyOpportunities: (id) => api.get(`/api/crm/companies/${id}/opportunities`),
  getCompanyTransactions: (id) => api.get(`/api/crm/companies/${id}/transactions`),

  // ── Opportunities ──
  listOpportunities: (params = {}) => api.get('/api/crm/opportunities', { params }),
  getOpportunity: (id) => api.get(`/api/crm/opportunities/${id}`),
  createOpportunity: (data) => api.post('/api/crm/opportunities', data),
  updateOpportunity: (id, data) => api.put(`/api/crm/opportunities/${id}`, data),
  deleteOpportunity: (id) => api.delete(`/api/crm/opportunities/${id}`),
  promoteOpportunity: (id) => api.post(`/api/crm/opportunities/${id}/promote`),

  // ── Transactions ──
  listTransactions: (params = {}) => api.get('/api/crm/transactions', { params }),
  getTransaction: (id) => api.get(`/api/crm/transactions/${id}`),
  createTransaction: (data) => api.post('/api/crm/transactions', data),
  updateTransaction: (id, data) => api.put(`/api/crm/transactions/${id}`, data),
  deleteTransaction: (id) => api.delete(`/api/crm/transactions/${id}`),

  // ── Batch (undo an import) ──
  deleteBatch: (batchId) => api.delete(`/api/crm/batches/${batchId}`),
};

// ==================== AUDIT TRAIL ====================

export const auditApi = {
  // Get audit logs (admin only)
  list: (params = {}) => api.get('/api/audit/logs', { params }),
  
  // Log an audit event
  log: (action, resource, details, userName) => 
    api.post('/api/audit/log', { action, resource, details, userName })
};

// ==================== AI CHAT ====================

export const chatApi = {
  // Send message
  send: (payload) =>
    api.post('/api/chat', payload),
  
  // Get deal context
  getDealContext: (dealId) => api.get(`/api/chat/deal/${dealId}/context`)
};

// ==================== INTEGRATIONS ====================

export const integrationsApi = {
  list: () => api.get('/api/integrations'),
  connectApiKey: ({ provider, apiKey, portcoId, accountId }) =>
    api.post('/api/integrations/connect/apikey', { provider, apiKey, portcoId, accountId }),
  disconnect: (provider) => api.post(`/api/integrations/disconnect/${provider}`),
};

// ==================== GRATA ====================

export const grataApi = {
  search: (q, limit = 20) => api.post('/api/integrations/grata/search', { q, limit }),
  similar: (companyId, limit = 20) => api.post('/api/integrations/grata/similar', { companyId, limit }),
  enrich: (companyId) => api.post('/api/integrations/grata/enrich', { companyId }),
};

export default api;
